import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { OutputCard } from "@/components/OutputCard";
import { Gauge, SplitSquareHorizontal, ShieldCheck, RefreshCw, Sparkles, Trophy, Trash2, Plus, Ban, TrendingUp, Eye, Users, Target } from "lucide-react";

const SUBS = [
  { id: "predict", label: "Performance Predictor", icon: Gauge },
  { id: "abtest", label: "A/B Test Lab", icon: SplitSquareHorizontal },
  { id: "analytics", label: "Performance Analytics", icon: TrendingUp },
  { id: "presets", label: "Compliance Presets", icon: ShieldCheck },
];

const scoreColor = (s) => (s >= 75 ? "#059669" : s >= 50 ? "#D97706" : "#DC2626");
const impactColor = { high: "bg-emerald-100 text-emerald-700", medium: "bg-amber-100 text-amber-700", low: "bg-slate-100 text-slate-600" };

function Ring({ score }) {
  const c = scoreColor(score);
  return (
    <div className="relative h-36 w-36 shrink-0" data-testid="predict-score-ring"
      style={{ borderRadius: "9999px", background: `conic-gradient(${c} ${score * 3.6}deg, #E2E8F0 0deg)` }}>
      <div className="absolute inset-[10px] rounded-full bg-white flex flex-col items-center justify-center">
        <span className="text-4xl font-bold" style={{ color: c }} data-testid="predict-score-value">{score}</span>
        <span className="text-xs text-slate-400">/ 100</span>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1"><span className="text-slate-500">{label}</span><span className="font-medium">{value}</span></div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${value}%`, background: scoreColor(value) }} /></div>
    </div>
  );
}

function Predictor() {
  const [copy, setCopy] = useState("");
  const [buyer, setBuyer] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const run = async () => {
    if (!copy.trim()) { toast.error("Paste some listing copy to analyze."); return; }
    setLoading(true); setRes(null);
    try {
      const { data } = await api.post("/intel/predict", { copy, target_buyer: buyer });
      setRes(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <div><Label>Listing copy</Label><Textarea data-testid="predict-copy" value={copy} onChange={(e) => setCopy(e.target.value)} rows={5} className="mt-1.5" placeholder="Paste a listing description to score…" /></div>
        <div><Label>Target buyer (optional)</Label><Input data-testid="predict-buyer" value={buyer} onChange={(e) => setBuyer(e.target.value)} className="mt-1.5" placeholder="first-time buyers" /></div>
        <Button onClick={run} disabled={loading} data-testid="predict-run-btn" className="h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold">
          {loading ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Analyzing…</> : <><Gauge className="h-4 w-4 mr-2" /> Predict performance</>}
        </Button>
      </div>

      {res && (
        <div className="space-y-5" data-testid="predict-result">
          <div className="bg-white border border-slate-200 rounded-lg p-6 flex flex-col sm:flex-row gap-6 items-center">
            <Ring score={res.score} />
            <div className="flex-1 w-full space-y-3">
              <div className="flex items-center gap-2"><span className="text-2xl font-bold" data-testid="predict-grade">{res.grade}</span><span className="text-sm text-slate-500">predicted engagement grade</span></div>
              <Metric label="Hook strength" value={res.hook_strength || 0} />
              <Metric label="Readability" value={res.readability || 0} />
              <Metric label="Emotional pull" value={res.emotional_pull || 0} />
            </div>
          </div>

          {res.strengths?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h3 className="font-semibold text-sm mb-3">Strengths</h3>
              <ul className="space-y-1.5">{res.strengths.map((s, i) => <li key={i} className="text-sm text-slate-700 flex gap-2"><span className="text-emerald-600">✓</span>{s}</li>)}</ul>
            </div>
          )}

          {res.improvements?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="predict-improvements">
              <h3 className="font-semibold text-sm mb-3">Improvement tips</h3>
              <ul className="space-y-2.5">{res.improvements.map((t, i) => (
                <li key={i} className="text-sm text-slate-700 flex items-start justify-between gap-3">
                  <span className="flex gap-2"><span className="text-slate-400">{i + 1}.</span>{t.tip}</span>
                  <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full shrink-0 ${impactColor[t.impact] || impactColor.low}`}>{t.impact}</span>
                </li>
              ))}</ul>
            </div>
          )}

          {res.rewrite && <OutputCard title="Higher-scoring rewrite" icon={Sparkles} testid="predict-rewrite" copyText={res.rewrite}>{res.rewrite}</OutputCard>}
        </div>
      )}
    </div>
  );
}

function ABLab() {
  const [copy, setCopy] = useState("");
  const [angle, setAngle] = useState("");
  const [buyer, setBuyer] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const run = async () => {
    if (!copy.trim()) { toast.error("Paste some listing copy to test."); return; }
    setLoading(true); setRes(null);
    try {
      const { data } = await api.post("/intel/abtest", { copy, angle, target_buyer: buyer });
      setRes(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const winner = res?.predicted_winner?.toUpperCase();
  const Card = ({ v, letter }) => (
    <div className={`rounded-lg border p-5 relative ${winner === letter ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200 bg-white"}`} data-testid={`abtest-variant-${letter.toLowerCase()}`}>
      {winner === letter && <span className="absolute -top-2.5 left-4 bg-emerald-600 text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full flex items-center gap-1"><Trophy className="h-3 w-3" /> Predicted winner</span>}
      <div className="flex items-center gap-2 mb-2"><span className="h-6 w-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">{letter}</span><span className="font-semibold text-sm">{v?.label}</span></div>
      <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{v?.copy}</p>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <div><Label>Listing copy</Label><Textarea data-testid="abtest-copy" value={copy} onChange={(e) => setCopy(e.target.value)} rows={5} className="mt-1.5" placeholder="Paste the copy you want to test…" /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Test angle</Label><Input data-testid="abtest-angle" value={angle} onChange={(e) => setAngle(e.target.value)} className="mt-1.5" placeholder="lifestyle vs. value" /></div>
          <div><Label>Target buyer</Label><Input data-testid="abtest-buyer" value={buyer} onChange={(e) => setBuyer(e.target.value)} className="mt-1.5" placeholder="investors" /></div>
        </div>
        <Button onClick={run} disabled={loading} data-testid="abtest-run-btn" className="h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold">
          {loading ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Building variants…</> : <><SplitSquareHorizontal className="h-4 w-4 mr-2" /> Generate A/B variants</>}
        </Button>
      </div>

      {res && (
        <div className="space-y-4" data-testid="abtest-result">
          <div className="grid md:grid-cols-2 gap-4"><Card v={res.variant_a} letter="A" /><Card v={res.variant_b} letter="B" /></div>
          <div className="bg-slate-900 text-white rounded-lg p-5">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Predicted lift · {res.lift_estimate}</p>
            <p className="text-sm leading-relaxed">{res.reasoning}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function Presets() {
  const [presets, setPresets] = useState(null);
  const [name, setName] = useState("");
  const [phrases, setPhrases] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/intel/presets"); setPresets(data); }
    catch { setPresets([]); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const list = phrases.split(/[\n,]/).map((p) => p.trim()).filter(Boolean);
    if (!name.trim() || list.length === 0) { toast.error("Add a name and at least one banned phrase."); return; }
    setSaving(true);
    try {
      await api.post("/intel/presets", { name, banned_phrases: list, active });
      toast.success("Preset saved — it'll auto-apply on every generation.");
      setName(""); setPhrases(""); setActive(true);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const toggle = async (p) => {
    try { await api.put(`/intel/presets/${p.id}`, { name: p.name, banned_phrases: p.banned_phrases, active: !p.active }); load(); }
    catch { toast.error("Couldn't update preset"); }
  };

  const del = async (id) => {
    try { await api.delete(`/intel/presets/${id}`); setPresets((l) => l.filter((x) => x.id !== id)); toast.success("Preset removed"); }
    catch { toast.error("Couldn't delete"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 h-fit">
        <div>
          <h3 className="font-semibold flex items-center gap-2"><Plus className="h-4 w-4" /> New compliance preset</h3>
          <p className="text-sm text-slate-500 mt-1">Banned phrases are forbidden from the AI and flagged if they slip through — applied to every active preset automatically.</p>
        </div>
        <div><Label>Preset name</Label><Input data-testid="preset-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" placeholder="Brokerage house rules" /></div>
        <div><Label>Banned phrases <span className="text-slate-400">(one per line or comma-separated)</span></Label>
          <Textarea data-testid="preset-phrases" value={phrases} onChange={(e) => setPhrases(e.target.value)} rows={5} className="mt-1.5" placeholder={"perfect for families\nsafe neighborhood\nexclusive community"} /></div>
        <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5">
          <span className="text-sm text-slate-700">Active (auto-apply on generation)</span>
          <Switch checked={active} onCheckedChange={setActive} data-testid="preset-active" />
        </div>
        <Button onClick={save} disabled={saving} data-testid="preset-save-btn" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold">
          {saving ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : "Save preset"}
        </Button>
      </div>

      <div className="space-y-3" data-testid="preset-list">
        {presets === null ? <div className="h-24 bg-white border border-slate-200 rounded-lg animate-pulse" /> :
          presets.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-500" data-testid="preset-empty">
              <ShieldCheck className="h-8 w-8 text-slate-300 mx-auto mb-3" /> No presets yet. Create one to enforce brokerage language rules on every generation.
            </div>
          ) : presets.map((p) => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`preset-${p.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`h-2 w-2 rounded-full ${p.active ? "bg-emerald-500" : "bg-slate-300"}`} />
                  <p className="font-medium truncate">{p.name}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Switch checked={p.active} onCheckedChange={() => toggle(p)} data-testid={`preset-toggle-${p.id}`} />
                  <button onClick={() => del(p.id)} data-testid={`preset-delete-${p.id}`} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {p.banned_phrases.map((ph, i) => (
                  <span key={i} className="text-xs bg-red-50 text-red-700 border border-red-100 rounded-full px-2 py-0.5 flex items-center gap-1"><Ban className="h-3 w-3" />{ph}</span>
                ))}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-slate-500 text-xs"><Icon className="h-4 w-4" /> {label}</div>
      <p className="text-3xl font-bold mt-2" style={{ color: accent || "#0F172A" }}>{value}</p>
    </div>
  );
}

function Analytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d) => {
    setLoading(true);
    try { const res = await api.get(`/intel/analytics?days=${d}`); setData(res.data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(days); }, [days]);

  if (loading && !data) return <div className="h-64 bg-white border border-slate-200 rounded-lg animate-pulse" />;
  if (!data) return null;

  const t = data.totals;
  const maxView = Math.max(1, ...data.timeseries.map((p) => p.views));

  return (
    <div className="space-y-6" data-testid="analytics-view">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={TrendingUp} label="Landing Pages" value={t.pages} />
        <Kpi icon={Eye} label="Total Views" value={t.views} accent="#0A66C2" />
        <Kpi icon={Users} label="Total Leads" value={t.leads} accent="#059669" />
        <Kpi icon={Target} label="Conversion" value={`${t.conversion}%`} accent="#D97706" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-semibold text-sm">Views & leads over time</h3>
            <p className="text-xs text-slate-500 mt-0.5">{data.window_views} views · {data.window_leads} leads in the last {data.window_days} days</p>
          </div>
          <div className="flex gap-1">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)} data-testid={`analytics-range-${d}`}
                className={`px-3 py-1 rounded-md text-xs font-medium ${days === d ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{d}d</button>
            ))}
          </div>
        </div>
        <div className="flex items-end gap-[3px] h-40 relative" data-testid="analytics-chart">
          {data.window_views === 0 && data.window_leads === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400" data-testid="analytics-chart-empty">No views or leads in this range yet.</div>
          ) : data.timeseries.map((p) => (
            <div key={p.date} className="flex-1 flex flex-col justify-end items-center group relative" title={`${p.date}: ${p.views} views, ${p.leads} leads`}>
              <div className="w-full rounded-t-sm bg-[#BFDBFE] relative" style={{ height: `${(p.views / maxView) * 100}%`, minHeight: p.views ? 3 : 0 }}>
                {p.leads > 0 && <div className="absolute bottom-0 inset-x-0 bg-[#059669] rounded-t-sm" style={{ height: `${(p.leads / Math.max(1, p.views)) * 100}%` }} />}
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-4 mt-4 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#BFDBFE]" /> Views</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#059669]" /> Leads</span>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-semibold text-sm mb-3">Top performing pages</h3>
        {data.top_pages.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="analytics-empty">No landing pages yet. Publish one (Landing Pages tab) and views/leads will show up here.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="analytics-top-table">
              <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                <th className="py-2 font-medium">Page</th><th className="py-2 font-medium text-right">Views</th><th className="py-2 font-medium text-right">Leads</th><th className="py-2 font-medium text-right">Conv.</th>
              </tr></thead>
              <tbody>{data.top_pages.map((p) => (
                <tr key={p.slug} className="border-b border-slate-50">
                  <td className="py-2.5 truncate max-w-[240px]">{p.title}</td>
                  <td className="py-2.5 text-right">{p.views}</td>
                  <td className="py-2.5 text-right">{p.leads}</td>
                  <td className="py-2.5 text-right font-medium">{p.conversion}%</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function IntelligenceView() {
  const [sub, setSub] = useState("predict");
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3">
        {SUBS.map((s) => (
          <button key={s.id} onClick={() => setSub(s.id)} data-testid={`intel-tab-${s.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${sub === s.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            <s.icon className="h-4 w-4" /> {s.label}
          </button>
        ))}
      </div>
      {sub === "predict" && <Predictor />}
      {sub === "abtest" && <ABLab />}
      {sub === "analytics" && <Analytics />}
      {sub === "presets" && <Presets />}
    </div>
  );
}
